// Shared fetch + download orchestration for the tracking modal. Owns the
// `getTracking` call (the panel is presentational and does no fetching) and
// binds the per-artifact sequencer to the session date so both apps wire in
// identically — they differ only in how they resolve the broadcast base URL,
// orgId, and session date.
import type {
  TrackingArtifact,
  TrackingResult
} from '@frontend/core/lib/domains/sessions/tracking'
import {
  downloadTrackingArtifacts,
  getTracking
} from '@frontend/core/lib/domains/sessions/tracking'
import { toast } from '@frontend/ui'
import { useCallback, useEffect, useState } from 'react'

/** What the panel renders against: the pending fetch, a throw, or a result. */
export type TrackingFetchState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; result: TrackingResult }

interface UseTrackingDownloadArgs {
  // `null` when the broadcast server hasn't resolved yet; never coerced to ''
  // — an empty base URL would resolve `/game/...` against the page origin and
  // fetch the wrong server, so a missing URL goes straight to the error state.
  baseUrl: string | null
  sessionId: string
  sessionDate: string
  orgId?: string
}

export function useTrackingDownload({
  baseUrl,
  sessionId,
  sessionDate,
  orgId
}: UseTrackingDownloadArgs) {
  const [state, setState] = useState<TrackingFetchState>({ status: 'loading' })

  const load = useCallback(async () => {
    setState({ status: 'loading' })
    if (!baseUrl) {
      setState({ status: 'error' })
      toast.error("Couldn't load tracking data. Try again.")

      return
    }
    try {
      const result = await getTracking(baseUrl, sessionId, { orgId })
      setState({ status: 'loaded', result })
    } catch {
      setState({ status: 'error' })
      toast.error("Couldn't load tracking data. Try again.")
    }
  }, [baseUrl, sessionId, orgId])

  useEffect(() => {
    void load()
  }, [load])

  const onDownload = useCallback(
    (artifacts: TrackingArtifact[], teamName: string) =>
      downloadTrackingArtifacts(artifacts, sessionDate, teamName),
    [sessionDate]
  )

  return { state, onDownload, retry: load }
}
